// Browser audio recording with RMS-based silence detection.
//
// iOS Safari requires a user gesture to unlock both AudioContext and the
// speech APIs. The Begin tap on the interview landing is that gesture; call
// primeMediaOnGesture() inside the tap handler exactly once to unlock both
// the AudioContext and SpeechSynthesis for the rest of the session.

import { lldbg } from "@/lib/lldbg"; // [LLDBG] TEMP

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/aac",
];

export class MicPermissionDeniedError extends Error {
  constructor() {
    super("microphone permission denied");
    this.name = "MicPermissionDeniedError";
  }
}

export class MicUnavailableError extends Error {
  constructor(reason: string) {
    super(`microphone unavailable: ${reason}`);
    this.name = "MicUnavailableError";
  }
}

export type RecordOptions = {
  maxDurationMs: number;
  silenceMs: number;
  silenceRmsThreshold?: number;
  audioContext: AudioContext;
  audioBitsPerSecond?: number;
  // Optional manual terminator (tier 2). Abort it from a UI Done/Stop button to
  // end the recording deterministically; it routes through the SAME stop path as
  // the silence / max-duration triggers, so there is exactly one finalize path.
  stopSignal?: AbortSignal;
  // Opt-in silence-gating (defaults preserve today's exact behavior).
  // requireSpeechBeforeSilence: when true, the trailing-silence timer does not
  // start until at least one frame has rms >= silenceRmsThreshold — so a quiet
  // lead-in before the subject speaks can never finalize the recording.
  requireSpeechBeforeSilence?: boolean;
  // minSpeechMs: a silence-triggered stop cannot fire before this many ms have
  // elapsed. Guards against an over-eager finalize on a soft/slow start.
  minSpeechMs?: number;
  // Per-CALLER mic constraints passed to getUserMedia({ audio: ... }). Default is
  // bare `true` (browser defaults: AGC on, no noise suppression) — the historical
  // behavior every caller relies on. The consent gates pass noise-rejecting
  // constraints; answer/voice-clone capture (QuestionFlow, FinalConsentRecording)
  // deliberately DO NOT, so clone-training audio stays at the raw default.
  // IGNORED when sharedStream is provided (the stream was already acquired with
  // its own constraints).
  audioConstraints?: MediaTrackConstraints | boolean;
  // Optional PRE-ACQUIRED stream to reuse across recordings (iOS re-prompts on
  // every fresh getUserMedia, so the gate phase acquires one stream on the Begin
  // gesture and reuses it). When provided: skip the internal getUserMedia AND do
  // NOT stop its tracks in cleanup — the caller owns the stream's lifetime. When
  // absent: unchanged — acquire a fresh stream and fully stop its tracks.
  sharedStream?: MediaStream;
};

export type RecordResult = {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  stoppedReason: "silence" | "max_duration" | "manual";
};

export const pickMimeType = (): string => {
  if (typeof MediaRecorder === "undefined") return "";
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return "";
};

export const primeMediaOnGesture = (): AudioContext => {
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctor();
  if (ctx.state === "suspended") {
    void ctx.resume();
  }
  if (typeof window.speechSynthesis !== "undefined") {
    // Calling getVoices() forces voice-list population on Safari; speaking a
    // tiny utterance inside the gesture unlocks subsequent .speak() calls for
    // the rest of the session.
    window.speechSynthesis.getVoices();
    const primer = new SpeechSynthesisUtterance(" ");
    primer.volume = 0;
    window.speechSynthesis.speak(primer);
  }
  return ctx;
};

// Acquire a mic stream up front (e.g. on a user gesture) so it can be REUSED
// across many recordings via RecordOptions.sharedStream — iOS Safari re-triggers
// the permission/active-mic flow on every fresh getUserMedia, so acquiring once
// avoids re-prompting per gate. Maps denial to MicPermissionDeniedError exactly
// like the inline acquire in recordWithSilenceDetection. The CALLER owns the
// returned stream and is responsible for stopping its tracks when done.
export const acquireMicStream = async (
  constraints: MediaTrackConstraints | boolean,
): Promise<MediaStream> => {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: constraints });
  } catch (err) {
    const name = (err as Error)?.name;
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      throw new MicPermissionDeniedError();
    }
    throw new MicUnavailableError(name ?? "getUserMedia failed");
  }
};

export const recordWithSilenceDetection = async (
  opts: RecordOptions,
): Promise<RecordResult> => {
  const {
    maxDurationMs,
    silenceMs,
    silenceRmsThreshold = 0.015,
    audioContext,
    audioBitsPerSecond,
    stopSignal,
    audioConstraints = true,
    requireSpeechBeforeSilence = false,
    minSpeechMs = 0,
    sharedStream,
  } = opts;

  if (typeof MediaRecorder === "undefined") {
    throw new MicUnavailableError("MediaRecorder not supported");
  }

  // A pre-acquired shared stream is REUSED (skip getUserMedia); its tracks are the
  // caller's to stop. Otherwise acquire a fresh stream we own and fully tear down.
  const ownsStream = sharedStream === undefined;
  let stream: MediaStream;
  if (sharedStream !== undefined) {
    stream = sharedStream;
  } else {
    try {
      // Mic constraints are PER-CALLER (audioConstraints; defaults to bare `true`).
      // Default `true` leaves autoGainControl / noiseSuppression / echoCancellation
      // at browser defaults (AGC on) — the historical behavior. The consent gates
      // opt into noise-rejecting constraints; answer / voice-clone capture keeps the
      // raw default so clone-training audio is untouched.
      stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    } catch (err) {
      const name = (err as Error)?.name;
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        throw new MicPermissionDeniedError();
      }
      throw new MicUnavailableError(name ?? "getUserMedia failed");
    }
  }

  const mimeType = pickMimeType();
  const recorderOpts: MediaRecorderOptions = {};
  if (mimeType) recorderOpts.mimeType = mimeType;
  if (audioBitsPerSecond) recorderOpts.audioBitsPerSecond = audioBitsPerSecond;
  const recorder = new MediaRecorder(stream, recorderOpts);
  const actualMime = recorder.mimeType || mimeType || "audio/webm";

  const chunks: Blob[] = [];
  recorder.addEventListener("dataavailable", (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  });

  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const buf = new Float32Array(analyser.fftSize);

  // [LLDBG] TEMP diagnostic — remove after the 6s-cutoff investigation
  const dbgTracks = stream.getAudioTracks(); // [LLDBG]
  lldbg("record:start", { // [LLDBG]
    audioContextState: audioContext.state, // [LLDBG]
    sampleRate: audioContext.sampleRate, // [LLDBG]
    silenceRmsThreshold, // [LLDBG]
    silenceMs, // [LLDBG]
    maxDurationMs, // [LLDBG]
    minSpeechMs, // [LLDBG]
    requireSpeechBeforeSilence, // [LLDBG]
    analyserFftSize: analyser.fftSize, // [LLDBG]
    streamTrackCount: dbgTracks.length, // [LLDBG]
    trackEnabled: dbgTracks[0]?.enabled, // [LLDBG]
    trackMuted: dbgTracks[0]?.muted, // [LLDBG]
    trackReadyState: dbgTracks[0]?.readyState, // [LLDBG]
  }); // [LLDBG]

  const startedAt = performance.now();
  let stoppedReason: "silence" | "max_duration" | "manual" = "silence";
  let silenceStartedAt: number | null = null;
  // Set once an above-threshold (speech) frame is seen. Only consulted when
  // requireSpeechBeforeSilence is on; otherwise it has no effect on behavior.
  let hasDetectedSpeech = false;
  let rafId = 0;
  let stopTimerId = 0;

  // [LLDBG] TEMP diagnostic — remove after the 6s-cutoff investigation
  let dbgPeakRms = 0; // [LLDBG]
  let dbgFrames = 0; // [LLDBG]
  let dbgFramesAboveThreshold = 0; // [LLDBG]
  let dbgFirstSpeechAtMs: number | null = null; // [LLDBG]

  const cleanup = () => {
    cancelAnimationFrame(rafId);
    clearTimeout(stopTimerId);
    source.disconnect();
    // Only stop tracks for a stream we acquired. A sharedStream is reused across
    // recordings and owned by the caller — stopping it would defeat the reuse.
    if (ownsStream) {
      stream.getTracks().forEach((t) => t.stop());
    }
  };

  const stopRecorderSafely = () => {
    if (recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {
        // already stopped
      }
    }
  };

  const completion = new Promise<RecordResult>((resolve, reject) => {
    recorder.addEventListener("stop", () => {
      cleanup();
      const durationMs = performance.now() - startedAt;
      const blob = new Blob(chunks, { type: actualMime });
      // [LLDBG] TEMP diagnostic — remove after the 6s-cutoff investigation
      lldbg("record:stop", { // [LLDBG]
        stoppedReason, // [LLDBG]
        durationMs, // [LLDBG]
        audioContextStateAtStop: audioContext.state, // [LLDBG]
        peakRms: dbgPeakRms, // [LLDBG]
        frames: dbgFrames, // [LLDBG]
        framesAboveThreshold: dbgFramesAboveThreshold, // [LLDBG]
        firstSpeechAtMs: dbgFirstSpeechAtMs, // [LLDBG]
        hasDetectedSpeech, // [LLDBG]
        blobBytes: blob.size, // [LLDBG]
        blobType: blob.type, // [LLDBG]
      }); // [LLDBG]
      resolve({ blob, mimeType: actualMime, durationMs, stoppedReason });
    });
    recorder.addEventListener("error", (e) => {
      cleanup();
      reject(new MicUnavailableError(`recorder error: ${(e as Event).type}`));
    });
  });

  const tick = () => {
    analyser.getFloatTimeDomainData(buf);
    let sumSquares = 0;
    for (let i = 0; i < buf.length; i++) sumSquares += buf[i] * buf[i];
    const rms = Math.sqrt(sumSquares / buf.length);

    const now = performance.now();
    const elapsed = now - startedAt;

    // [LLDBG] TEMP frame accounting — remove after the 6s-cutoff investigation
    dbgFrames++; // [LLDBG]
    if (rms > dbgPeakRms) dbgPeakRms = rms; // [LLDBG]
    if (rms >= silenceRmsThreshold) { // [LLDBG]
      dbgFramesAboveThreshold++; // [LLDBG]
      if (dbgFirstSpeechAtMs === null) dbgFirstSpeechAtMs = elapsed; // [LLDBG]
    } // [LLDBG]

    if (rms >= silenceRmsThreshold) {
      hasDetectedSpeech = true;
    }

    // Gate the trailing-silence timer: when requireSpeechBeforeSilence is on, do
    // not begin accumulating silence until speech has actually been heard (a quiet
    // lead-in keeps silenceStartedAt null instead of counting down). With the
    // default (off) this is always true, so behavior is unchanged.
    const silenceCountsNow = !requireSpeechBeforeSilence || hasDetectedSpeech;

    if (rms < silenceRmsThreshold && silenceCountsNow) {
      if (silenceStartedAt === null) silenceStartedAt = now;
      // minSpeechMs (default 0) is a floor on how soon a silence stop may fire.
      if (now - silenceStartedAt >= silenceMs && elapsed >= minSpeechMs) {
        stoppedReason = "silence";
        stopRecorderSafely();
        return;
      }
    } else {
      silenceStartedAt = null;
    }

    if (elapsed >= maxDurationMs) {
      stoppedReason = "max_duration";
      stopRecorderSafely();
      return;
    }

    rafId = requestAnimationFrame(tick);
  };

  stopTimerId = window.setTimeout(() => {
    stoppedReason = "max_duration";
    stopRecorderSafely();
  }, maxDurationMs + 500);

  recorder.start();
  rafId = requestAnimationFrame(tick);

  // Manual terminator (tier 2): a Done/Stop button aborts stopSignal. Route it
  // through the SAME stopRecorderSafely -> "stop" event -> cleanup/resolve path
  // as the silence / max-duration triggers, so there is exactly one finalize path.
  if (stopSignal) {
    const onAbort = () => {
      stoppedReason = "manual";
      stopRecorderSafely();
    };
    if (stopSignal.aborted) onAbort();
    else stopSignal.addEventListener("abort", onAbort, { once: true });
  }

  return completion;
};
